import { useMemo } from 'react';
import { Button, Card, Col, Form, Modal, Row, Stack } from 'react-bootstrap';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import CustomModal from 'src/components/custom_components/CustomModal/CustomModal';

import { Api } from 'src/models/Api.model';
import { I18NextNS } from 'src/models/Language.model';
import { PatientInfoKeyMapOptain, Session } from 'src/models/Session.model';

import useGetSessions from 'src/hooks/useGetSessions';

interface ResumeInformationModalProps {
  currentSession: Session;
  isShowResumeModal: boolean;
  handleSetIsShowResumeModal: (isShowResumeModal: boolean) => void;
}

export function ResumeInformationModal({
  currentSession,
  isShowResumeModal,
  handleSetIsShowResumeModal,
}: ResumeInformationModalProps) {
  const history = useHistory();

  const { setBackendStatus, setSession } = useRemoteBoundStore();

  const { getSessions } = useGetSessions();

  const { t } = useTranslation(I18NextNS.COMMON);

  const patientInfoConfirmation = useMemo(() => {
    const { id, firstName, lastName, dob } = currentSession;
    return { id, firstName, lastName, dob };
  }, [currentSession]);

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = useForm();

  const onSubmit: SubmitHandler<object> = async () => {
    if (!currentSession.sessionId) {
      throw new Error(t('message.error.device-data-id-is-required'));
    }
    const backendState = await Api.remote.get_backend_state(
      currentSession.sessionId,
    );
    setBackendStatus(backendState);

    await getSessions(currentSession.sessionId);

    setSession(currentSession);

    history.push({
      pathname:
        currentSession.typeOfDiabetes === undefined
          ? '/diabetes-data'
          : '/session-preview',
      state: {
        isResume: true,
        leftImageFileName: currentSession.leftImageFileName,
        rightImageFileName: currentSession.rightImageFileName,
      },
    });
  };

  return (
    <CustomModal
      show={isShowResumeModal}
      onHide={() => handleSetIsShowResumeModal(false)}
      scrollable={true}
      fullscreen="xl-down"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {t('patientFields.confirmPatientInformation')}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          <Card className="mb-0">
            <Card.Body>
              <Stack gap={2}>
                {(
                  Object.keys(patientInfoConfirmation) as Array<
                    keyof Session & keyof typeof PatientInfoKeyMapOptain
                  >
                ).map((objKey) => (
                  <Row key={objKey}>
                    <Col>
                      <span className="fw-medium">
                        {t(`patientFields.${PatientInfoKeyMapOptain[objKey]}`)}
                      </span>
                    </Col>
                    <Col>
                      <span>{patientInfoConfirmation[objKey]}</span>
                    </Col>
                  </Row>
                ))}
              </Stack>
            </Card.Body>
          </Card>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="primary-100"
            onClick={() => handleSetIsShowResumeModal(false)}
          >
            {t('modal.cancel')}
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? t('modal.confirming') : t('modal.confirm')}
          </Button>
        </Modal.Footer>
      </Form>
    </CustomModal>
  );
}
